import { ReactNode } from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { cn } from "~/lib/utils";

export interface SectionSidebarProps {
  ariaLabel: string;
  children: ReactNode;
}

interface EdgeToggleProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  placement: "top-left" | "divider";
  isRevealedOnHover?: boolean;
}

const EdgeToggle = ({
  label,
  icon,
  onClick,
  placement,
  isRevealedOnHover = false,
}: EdgeToggleProps) => {
  return (
    <Tooltip delayDuration={700}>
      <TooltipTrigger asChild>
        <Button
          variant={placement === "divider" ? "outline" : "ghost"}
          size="icon"
          onClick={onClick}
          aria-label={label}
          className={cn(
            "absolute z-10",
            placement === "top-left"
              ? "left-2 top-2"
              : "left-1/2 top-3 size-6 -translate-x-1/2 rounded-md bg-background",
            isRevealedOnHover &&
              "opacity-0 transition-opacity focus-visible:opacity-100 group-hover/edge:opacity-100"
          )}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Toggle Navigation</TooltipContent>
    </Tooltip>
  );
};

export const SectionSidebar = ({ ariaLabel, children }: SectionSidebarProps) => {
  const [preferences, updatePreferences] = useUserPreferences();
  const isCollapsed = preferences.collapseSectionSidebar ?? false;

  const setCollapsed = (collapsed: boolean) => {
    updatePreferences({ collapseSectionSidebar: collapsed });
  };

  return (
    <div
      className={cn(
        "relative h-full shrink-0 transition-[width] duration-300 ease-in-out",
        isCollapsed ? "w-0" : "w-56"
      )}
    >
      <nav
        aria-label={ariaLabel}
        aria-hidden={isCollapsed}
        inert={isCollapsed}
        className={cn(
          "h-full overflow-hidden bg-sidebar",
          isCollapsed ? "border-r-0" : "border-r border-border"
        )}
      >
        {/* Fixed to the expanded width so content is clipped, not squeezed, while
            the wrapper animates. */}
        <div className="flex h-full w-56 flex-col">{children}</div>
      </nav>

      {isCollapsed ? (
        <EdgeToggle
          label="Expand navigation"
          icon={<PanelLeftOpen className="size-4" />}
          onClick={() => setCollapsed(false)}
          placement="top-left"
        />
      ) : (
        <div className="group/edge absolute inset-y-0 -right-5 z-10 w-10">
          <EdgeToggle
            label="Collapse navigation"
            icon={<PanelLeftClose className="size-3.5" />}
            onClick={() => setCollapsed(true)}
            placement="divider"
            isRevealedOnHover
          />
        </div>
      )}
    </div>
  );
};
